import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { SpotifySearchSection } from './SpotifySearchSection';
import { FileUploadSection } from './FileUploadSection';
import { useAdminSequences, SequenceWithMetadata } from '@/hooks/useAdminSequences';
import { spotifyService, SpotifyTrack } from '@/services/spotifyService';
import { toast } from 'sonner';
import { Plus, Save, RefreshCw, Music, Upload, AlertTriangle, CheckCircle, ExternalLink, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface SequenceEditorProps {
  preselectedSequenceId?: string | null;
}

export function SequenceEditor({ preselectedSequenceId }: SequenceEditorProps) {
  const {
    sequences,
    artists,
    genres,
    loading,
    createSequence,
    updateSequence,
    createArtist,
    createGenre,
    uploadSequenceFile,
    findDuplicateSequences
  } = useAdminSequences();

  const [selectedSequenceId, setSelectedSequenceId] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<SequenceWithMetadata>>({});
  const [spotifyData, setSpotifyData] = useState<SpotifyTrack | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingArtist, setPendingArtist] = useState<string | null>(null);
  const [pendingGenre, setPendingGenre] = useState<string | null>(null);
  const [artistOpen, setArtistOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const justSavedRef = useRef(false);

  // Track if form has been initialized for the current sequence
  const initializedSequenceRef = useRef<string | null>(null);

  // Update form data when sequences list changes and we have a selected sequence
  // BUT NOT during save operations or immediately after to avoid overriding user changes
  useEffect(() => {
    if (selectedSequenceId && !isCreating && !saving && !justSavedRef.current) {
      const sequence = sequences.find(s => s.id === selectedSequenceId);
      if (sequence) {
        // Only sync form data if:
        // 1. We haven't initialized this sequence yet, OR
        // 2. The sequence data has been updated from the server (after save)
        const shouldSync = 
          initializedSequenceRef.current !== selectedSequenceId ||
          (justSavedRef.current === false && initializedSequenceRef.current === selectedSequenceId);
        
        if (shouldSync) {
          console.log('Syncing form with DB values for sequence:', selectedSequenceId);
          setFormData(sequence);
          initializedSequenceRef.current = selectedSequenceId;
        }
      }
    }
    
    // Reset the justSaved flag after a delay to allow for future syncing
    if (justSavedRef.current) {
      const timer = setTimeout(() => {
        justSavedRef.current = false;
      }, 2000); // Wait 2 seconds before allowing form sync again
      
      return () => clearTimeout(timer);
    }
  }, [sequences, selectedSequenceId, isCreating, saving]);

  // Reset form when switching sequences
  const handleSequenceSelect = useCallback((sequenceId: string) => {
    if (sequenceId === 'new') {
      setIsCreating(true);
      setSelectedSequenceId('');
      setFormData({
        name: '',
        title: '',
        description: '',
        price: 0,
        is_free: true,
        software_type: 'xLights'
      });
      setSpotifyData(null);
      initializedSequenceRef.current = null; // Reset initialization tracking
    } else {
      setIsCreating(false);
      setSelectedSequenceId(sequenceId);
      const sequence = sequences.find(s => s.id === sequenceId);
      if (sequence) {
        setFormData(sequence);
        initializedSequenceRef.current = sequenceId; // Mark as initialized
      }
      setSpotifyData(null);
    }
    setPendingArtist(null);
    setPendingGenre(null);
  }, [sequences]);

  // Handle preselected sequence from URL parameter
  useEffect(() => {
    if (preselectedSequenceId && sequences.length > 0 && !selectedSequenceId) {
      const sequence = sequences.find(s => s.id === preselectedSequenceId);
      if (sequence) {
        console.log('Auto-selecting sequence from URL parameter:', preselectedSequenceId);
        handleSequenceSelect(preselectedSequenceId);
      } else {
        console.warn('Preselected sequence not found:', preselectedSequenceId);
      }
    }
  }, [preselectedSequenceId, sequences, selectedSequenceId, handleSequenceSelect]);

  // Handle Spotify track selection
  const handleSpotifySelect = async (track: SpotifyTrack) => {
    setSpotifyData(track);
    
    // Auto-populate fields from Spotify
    const updates = {
      ...formData,
      title: `${track.name} - ${track.artists[0]?.name}`,
      album: track.album.name,
      image_url: track.album.images.find(img => img.width === 640)?.url || track.album.images[0]?.url,
      spotify_track_id: track.id,
      spotify_explicit: track.explicit,
      spotify_preview_url: track.preview_url,
      spotify_duration_ms: track.duration_ms,
      spotify_popularity: track.popularity,
      duration: Math.round(track.duration_ms / 1000)
    };

    // Check if artist exists
    const artistName = track.artists[0]?.name;
    if (artistName) {
      // Helper function to normalize artist names for comparison
      const normalizeArtistName = (name: string) => {
        return name
          .toLowerCase()
          .replace(/[-\s]+/g, ' ') // Replace hyphens and multiple spaces with single space
          .replace(/[^\w\s]/g, '') // Remove punctuation except word chars and spaces
          .trim();
      };

      const normalizedSpotifyName = normalizeArtistName(artistName);
      
      const existingArtist = artists.find(a => {
        const normalizedDbName = normalizeArtistName(a.name);
        return normalizedDbName === normalizedSpotifyName;
      });
      
      if (existingArtist) {
        updates.artist_id = existingArtist.id;
        console.log(`Found existing artist: "${existingArtist.name}" (${existingArtist.id}) for Spotify artist: "${artistName}"`);
      } else {
        setPendingArtist(artistName);
        console.log(`No existing artist found for: "${artistName}". Normalized: "${normalizedSpotifyName}"`);
      }
    }

    setFormData(updates);
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: unknown) => {
    // Convert empty strings to null for optional fields
    const processedValue = (typeof value === 'string' && value.trim() === '') ? null : value;
    
    console.log(`Field changed: ${field} = "${value}" -> processed: ${processedValue}`);
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Debug function to test direct Supabase update
  const testDirectUpdate = async () => {
    if (!selectedSequenceId) return;
    
    try {
      console.log('Testing direct Supabase update...');
      
      // Test 1: Check current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Current authenticated user:', user?.id, userError);
      
      // Test 2: Try direct update
      const { data, error } = await supabase
        .from('sequences')
        .update({ 
          description: 'Direct test update - ' + new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSequenceId)
        .select();
      
      console.log('Direct update result:', { data, error });
      
      if (error) {
        console.error('Direct update failed:', error);
        alert(`Update failed: ${error.message}`);
      } else {
        console.log('Direct update succeeded:', data);
        alert('Direct update succeeded! Check console for details.');
      }
    } catch (err) {
      console.error('Test failed:', err);
      alert(`Test failed: ${err}`);
    }
  };

  // Create pending artist
  const handleCreateArtist = async () => {
    if (!pendingArtist) return;
    
    try {
      const artist = await createArtist(
        pendingArtist,
        `Artist for ${pendingArtist}`,
        spotifyData?.album.images[0]?.url
      );
      
      setFormData(prev => ({
        ...prev,
        artist_id: artist.id
      }));
      
      setPendingArtist(null);
      toast.success(`Artist "${pendingArtist}" created successfully`);
    } catch (error: any) {
      console.error('Error creating artist:', error);
      
      // Handle duplicate artist error
      if (error?.code === '23505' || error?.message?.includes('duplicate key value')) {
        // Artist already exists, try to find it
        const normalizeArtistName = (name: string) => {
          return name
            .toLowerCase()
            .replace(/[-\s]+/g, ' ')
            .replace(/[^\w\s]/g, '')
            .trim();
        };

        const normalizedPendingName = normalizeArtistName(pendingArtist);
        
        const existingArtist = artists.find(a => {
          const normalizedDbName = normalizeArtistName(a.name);
          return normalizedDbName === normalizedPendingName;
        });

        if (existingArtist) {
          setFormData(prev => ({
            ...prev,
            artist_id: existingArtist.id
          }));
          
          setPendingArtist(null);
          toast.success(`Found and selected existing artist: "${existingArtist.name}"`);
        } else {
          toast.error(`Artist "${pendingArtist}" already exists but couldn't be found. Please refresh and try again.`);
        }
      } else {
        toast.error('Failed to create artist');
      }
    }
  };

  // Create pending genre
  const handleCreateGenre = async () => {
    if (!pendingGenre) return;
    
    try {
      const genre = await createGenre(pendingGenre);
      
      setFormData(prev => ({
        ...prev,
        genre_id: genre.id
      }));
      
      setPendingGenre(null);
      toast.success(`Genre "${pendingGenre}" created successfully`);
    } catch (error) {
      toast.error('Failed to create genre');
      console.error('Error creating genre:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!selectedSequenceId && !isCreating) {
      toast.error('Please select or create a sequence first');
      return;
    }

    try {
      const sequenceId = selectedSequenceId || 'temp-' + Date.now();
      const result = await uploadSequenceFile(file, sequenceId);
      
      setFormData(prev => ({
        ...prev,
        file_url: result.url
      }));
      
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
      console.error('Error uploading file:', error);
    }
  };

  // Save sequence
  const handleSave = async () => {
    console.log('=== SAVE BUTTON CLICKED ===');
    console.log('Form data being saved:', formData);
    console.log('Selected sequence ID:', selectedSequenceId);
    console.log('Is creating:', isCreating);
    
    if (!formData.name || !formData.title) {
      toast.error('Name and title are required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        console.log('Creating new sequence...');
        // Create new sequence
        const newSequence = await createSequence(formData);
        console.log('New sequence created:', newSequence);
        toast.success('Sequence created successfully');
        
        // Switch to editing the newly created sequence
        setIsCreating(false);
        setSelectedSequenceId(newSequence.id);
        justSavedRef.current = true; // Prevent form data override after creation
      } else if (selectedSequenceId) {
        console.log('Updating existing sequence...');
        // Update existing sequence
        const result = await updateSequence(selectedSequenceId, formData);
        console.log('Sequence update result:', result);
        toast.success('Sequence updated successfully');
        
        justSavedRef.current = true; // Prevent form data override after update
      }
    } catch (error) {
      toast.error('Failed to save sequence');
      console.error('Error saving sequence:', error);
    } finally {
      setSaving(false);
      console.log('=== SAVE COMPLETED ===');
    }
  };

  const selectedSequence = sequences.find(s => s.id === selectedSequenceId);

  return (
    <div className="space-y-6">
      {/* Sequence Selector */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="sequence-select" className="text-base font-medium">
            Select Sequence to Edit
          </Label>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {sequences.length} sequences available
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const duplicates = await findDuplicateSequences();
                if (duplicates.length > 0) {
                  const message = duplicates.map(d => `"${d.title}" (${d.sequences.length} copies)`).join('\n');
                  alert(`Found ${duplicates.length} sets of duplicate sequences:\n\n${message}`);
                } else {
                  alert('No duplicate sequences found!');
                }
              }}
              className="text-xs"
            >
              Check Duplicates
            </Button>
            {selectedSequenceId && (
              <Button
                variant="outline"
                size="sm"
                onClick={testDirectUpdate}
                className="text-xs bg-red-50 border-red-200 text-red-700"
              >
                Test Update
              </Button>
            )}
          </div>
        </div>
        
        <Popover open={sequenceOpen} onOpenChange={setSequenceOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={sequenceOpen}
              className="w-full justify-between"
            >
              {selectedSequenceId 
                ? sequences.find(s => s.id === selectedSequenceId)?.title || sequences.find(s => s.id === selectedSequenceId)?.name
                : isCreating
                  ? "Create New Sequence"
                  : "Select a sequence or create new..."
              }
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput placeholder="Search sequences..." />
              <CommandEmpty>No sequence found.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  <CommandItem
                    value="new"
                    onSelect={() => {
                      handleSequenceSelect('new');
                      setSequenceOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isCreating ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Sequence
                  </CommandItem>
                  {sequences
                    .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name))
                    .map((sequence) => (
                    <CommandItem
                      key={sequence.id}
                      value={`${sequence.title || sequence.name} ${sequence.name} ${sequence.id.slice(0, 8)}`}
                      onSelect={() => {
                        handleSequenceSelect(sequence.id);
                        setSequenceOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedSequenceId === sequence.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate">{sequence.title || sequence.name}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            ID: {sequence.id.slice(0, 8)}... • Created: {new Date(sequence.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex gap-1 ml-2 flex-shrink-0">
                          {sequence.spotify_track_id && (
                            <Badge variant="secondary" className="text-xs">S</Badge>
                          )}
                          {sequence.artist_id && (
                            <Badge variant="secondary" className="text-xs">A</Badge>
                          )}
                          {sequence.genre_id && (
                            <Badge variant="secondary" className="text-xs">G</Badge>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Preview Button - only show when editing existing sequence */}
      {selectedSequenceId && !isCreating && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/sequence/${selectedSequenceId}`, '_blank')}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Preview on Site
          </Button>
        </div>
      )}

      {(selectedSequenceId || isCreating) && (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Form Fields */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Sequence Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      placeholder="Sequence identifier"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title || ''}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      placeholder="Display title"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Sequence description"
                    rows={3}
                  />
                </div>

                {/* Artist and Genre */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="artist">Artist</Label>
                    {pendingArtist ? (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Artist "{pendingArtist}" doesn't exist.
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            onClick={handleCreateArtist}
                          >
                            Create Artist
                          </Button>
                        </AlertDescription>
                      </Alert>
                    ) : (
                                              <Popover open={artistOpen} onOpenChange={setArtistOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={artistOpen}
                              className="w-full justify-between"
                            >
                              {formData.artist_id 
                                ? artists.find(a => a.id === formData.artist_id)?.name 
                                : "Select artist..."
                              }
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0">
                            <Command>
                              <CommandInput placeholder="Search artist..." />
                              <CommandEmpty>No artist found.</CommandEmpty>
                              <CommandList>
                                <CommandGroup>
                                  {artists
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((artist) => (
                                    <CommandItem
                                      key={artist.id}
                                      value={artist.name}
                                      onSelect={() => {
                                        handleFieldChange('artist_id', artist.id);
                                        setArtistOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          formData.artist_id === artist.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {artist.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="genre">Genre</Label>
                    <Popover open={genreOpen} onOpenChange={setGenreOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={genreOpen}
                          className="w-full justify-between"
                        >
                          {formData.genre_id 
                            ? genres.find(g => g.id === formData.genre_id)?.name 
                            : "Select genre..."
                          }
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search genre..." />
                          <CommandEmpty>No genre found.</CommandEmpty>
                          <CommandList>
                            <CommandGroup>
                              {genres
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((genre) => (
                                <CommandItem
                                  key={genre.id}
                                  value={genre.name}
                                  onSelect={() => {
                                    handleFieldChange('genre_id', genre.id);
                                    setGenreOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.genre_id === genre.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {genre.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Pricing and Software */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price">Price ($)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price || 0}
                      onChange={(e) => handleFieldChange('price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (sec)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration || ''}
                      onChange={(e) => handleFieldChange('duration', parseInt(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="software">Software</Label>
                    <Select 
                      value={formData.software_type || ''} 
                      onValueChange={(value) => handleFieldChange('software_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xLights">xLights</SelectItem>
                        <SelectItem value="Light-O-Rama">Light-O-Rama</SelectItem>
                        <SelectItem value="Vixen">Vixen</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Holiday and File Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="holiday_type">Holiday</Label>
                    <Select 
                      value={formData.holiday_type || ''} 
                      onValueChange={(value) => handleFieldChange('holiday_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select holiday..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Christmas">Christmas</SelectItem>
                        <SelectItem value="Halloween">Halloween</SelectItem>
                        <SelectItem value="FourthOfJuly">4th of July</SelectItem>
                        <SelectItem value="Easter">Easter</SelectItem>
                        <SelectItem value="Thanksgiving">Thanksgiving</SelectItem>
                        <SelectItem value="Hanukkah">Hanukkah</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="file_type">File Type</Label>
                    <Select 
                      value={formData.file_type || ''} 
                      onValueChange={(value) => handleFieldChange('file_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select file type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sequence only">Sequence only</SelectItem>
                        <SelectItem value="Lyrics only">Lyrics only</SelectItem>
                        <SelectItem value="Sequence & Lyrics">Sequence & Lyrics</SelectItem>
                        <SelectItem value="Singing Face">Singing Face</SelectItem>
                        <SelectItem value="Matrix Video">Matrix Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Media URLs */}
                <div className="space-y-4">
                  <Label className="text-base font-medium">Media URLs</Label>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="image_url">Image URL</Label>
                      <Input
                        id="image_url"
                        value={formData.image_url || ''}
                        onChange={(e) => handleFieldChange('image_url', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="video_url">Video URL</Label>
                      <Input
                        id="video_url"
                        value={formData.video_url || ''}
                        onChange={(e) => handleFieldChange('video_url', e.target.value)}
                        placeholder="https://example.com/video.mp4"
                      />
                    </div>
                  </div>
                </div>

                {/* Spotify Data */}
                {spotifyData && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800">Spotify Data Applied</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Album: {spotifyData.album.name}</p>
                      <p>Popularity: {spotifyData.popularity}/100</p>
                      <p>Duration: {Math.round(spotifyData.duration_ms / 1000)}s</p>
                      {spotifyData.explicit && <Badge variant="secondary">Explicit</Badge>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* File Upload */}
            <FileUploadSection
              currentFileUrl={formData.file_url}
              onFileUpload={handleFileUpload}
              disabled={!selectedSequenceId && !isCreating}
            />

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.title}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isCreating ? 'Create Sequence' : 'Update Sequence'}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right Side - Spotify Search */}
          <div>
            <SpotifySearchSection
              onTrackSelect={handleSpotifySelect}
              currentSequence={selectedSequence}
              defaultSearchTerm={formData.title}
            />
          </div>
        </div>
      )}
    </div>
  );
}